/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/itp-export/useQaItpOnedrive.ts
 * FILE_ID: JOBHUB-QA-ITP-ONEDRIVE-001
 * PURPOSE: Turn the OneDrive checkbox into an admin-approval panel when consent is blocked.
 */
import { useEffect, useState } from 'react'
import type { QaApi } from '../../../qa/qaApi'
import { mailboxForFrom, startOnedriveConsent } from './startOnedriveConsent'
import type { useQaItpFrom } from './useQaItpFrom'

export interface OnedriveApproval {
  email: string
  adminConsentUrl: string
}

type StatusApi = Partial<Pick<QaApi, 'itpOnedriveStatus'>>

export function useQaItpOnedrive({
  api,
  inspectionId,
  client,
  fromModel,
  setError,
  reportError,
}: {
  api: StatusApi
  inspectionId: string
  client: { get: (url: string) => Promise<{ data?: { authorization_url?: string } }> }
  fromModel: Pick<ReturnType<typeof useQaItpFrom>, 'from' | 'sendable' | 'userEmail'>
  setError: (value: string | null) => void
  reportError: (err: unknown) => void
}) {
  const [shareOnedrive, setShareFlag] = useState(false)
  const [driveAccountId, setDriveAccountId] = useState<number | null>(null)
  const [approval, setApproval] = useState<OnedriveApproval | null>(null)

  useEffect(() => {
    setShareFlag(false)
    setDriveAccountId(null)
    setApproval(null)
  }, [fromModel.from])

  const showApproval = async (email: string, accountId: number | null) => {
    let adminConsentUrl = ''
    if (accountId != null && api.itpOnedriveStatus) {
      const status = await api.itpOnedriveStatus(inspectionId, accountId)
      adminConsentUrl = status.admin_consent_url || ''
    }
    setShareFlag(false)
    setDriveAccountId(null)
    setApproval({ email, adminConsentUrl })
  }

  const setShareOnedrive = async (on: boolean) => {
    if (!on) {
      setShareFlag(false)
      setDriveAccountId(null)
      setApproval(null)
      return
    }
    const picked = mailboxForFrom(fromModel.from, fromModel.sendable, fromModel.userEmail)
    if ('error' in picked) {
      setError(picked.error)
      return
    }
    try {
      if (picked.accountId == null || !api.itpOnedriveStatus) {
        await startOnedriveConsent(client, picked.email)
        setError('Sign in to the mailbox selected in From, then send again.')
        return
      }
      const status = await api.itpOnedriveStatus(inspectionId, picked.accountId)
      if (!status.ready) {
        setApproval({ email: picked.email, adminConsentUrl: status.admin_consent_url || '' })
        return
      }
      setApproval(null)
      setDriveAccountId(picked.graph ? null : picked.accountId)
      setShareFlag(true)
    } catch (err) {
      reportError(err)
    }
  }

  const onConsentRequired = async () => {
    const picked = mailboxForFrom(fromModel.from, fromModel.sendable, fromModel.userEmail)
    if ('error' in picked) return
    try {
      await showApproval(picked.email, picked.accountId)
    } catch (err) {
      reportError(err)
    }
  }

  return {
    shareOnedrive,
    setShareOnedrive,
    driveAccountId,
    approval,
    signIn: () => {
      const picked = mailboxForFrom(fromModel.from, fromModel.sendable, fromModel.userEmail)
      if (!('error' in picked)) void startOnedriveConsent(client, picked.email)
    },
    copyLink: () => {
      const url = approval?.adminConsentUrl || ''
      if (url) void navigator.clipboard.writeText(url)
    },
    useAttachment: () => {
      setShareFlag(false)
      setDriveAccountId(null)
      setApproval(null)
    },
    onConsentRequired,
  }
}
